import React, { useCallback } from 'react';

import { Text } from '../../../../components/Text/Text';
import { Skeleton } from '../../../../components/Skeleton';
import { LUMIO } from '../../../../theme/tokens';
import { FireIcon, AcademicCapIcon } from './icons';
import {
  Section,
  SectionLabel,
  SectionLabelText,
  Cards,
  Card,
  CardText,
  ErrorRow,
  ErrorText,
  RetryButton,
} from './LumioChat.styles';

export interface SuggestionCardsProps {
  /** Backend `suggested_topics` (also the prompt sent when a card is picked). */
  topics: string[];
  /** The `/chat/init` fetch is in flight. */
  loading: boolean;
  /** The fetch failed; show an honest error with a retry (never fake cards). */
  error: boolean;
  /** Re-attempt the fetch. */
  onRetry: () => void;
  /** Send the picked topic as a prompt. */
  onPick: (topic: string) => void;
}

// Card geometry from Figma (4662:363): a full-width, 64px, 20px-radius block.
const SKELETON_CARD = { height: 64, radius: 20 } as const;
// Match the usual backend split: one HOT TOPIC, three DEEP DIVE placeholders.
const DEEP_DIVE_PLACEHOLDERS = [0, 1, 2];

/**
 * The welcome-screen suggestion cards, sourced ONLY from the backend
 * `suggested_topics` (`/chat/init`). There is deliberately no static fallback:
 * while loading we show skeletons, on failure an error with a retry. The first
 * topic fills the HOT TOPICS slot, the rest fill DEEP DIVE. Shared by the desktop
 * panel and the mobile overlay (same styled-components).
 */
export const SuggestionCards = ({
  topics,
  loading,
  error,
  onRetry,
  onPick,
}: SuggestionCardsProps): React.ReactElement => {
  const onCardKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, topic: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onPick(topic);
      }
    },
    [onPick],
  );

  if (error) {
    return (
      <ErrorRow role="alert">
        <ErrorText text="Couldn’t load suggestions." />
        <RetryButton type="button" onClick={onRetry}>
          <Text text="Retry" />
        </RetryButton>
      </ErrorRow>
    );
  }

  const renderCard = (topic: string): React.ReactElement => (
    <Card
      key={topic}
      role="button"
      tabIndex={0}
      onClick={() => onPick(topic)}
      onKeyDown={(event) => onCardKeyDown(event, topic)}
    >
      <CardText text={topic} />
    </Card>
  );

  const [hot, ...deep] = topics;
  // Show skeletons while the fetch is in flight AND on the first paint before it
  // starts (topics still empty, no error yet) — so the welcome never flashes bare
  // section headers. A resolved-but-empty response is handled as an error above.
  const showSkeletons = loading || topics.length === 0;

  return (
    <React.Fragment>
      <Section>
        <SectionLabel $color={LUMIO.hotTopics}>
          <FireIcon />
          <SectionLabelText text="HOT TOPICS" />
        </SectionLabel>
        {showSkeletons ? (
          <Skeleton block height={SKELETON_CARD.height} radius={SKELETON_CARD.radius} />
        ) : (
          hot && renderCard(hot)
        )}
      </Section>
      <Section>
        <SectionLabel $color={LUMIO.deepDive}>
          <AcademicCapIcon />
          <SectionLabelText text="DEEP DIVE" />
        </SectionLabel>
        <Cards>
          {showSkeletons
            ? DEEP_DIVE_PLACEHOLDERS.map((i) => (
                <Skeleton key={i} block height={SKELETON_CARD.height} radius={SKELETON_CARD.radius} />
              ))
            : deep.map(renderCard)}
        </Cards>
      </Section>
    </React.Fragment>
  );
};

export default SuggestionCards;
